import { useIntl } from 'react-intl';
import { Schema } from 'library/types';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import type { TABLE_SCHEMA_PAGE_NAME_ENUM } from 'enums';
import { useCallback, useEffect, useRef, useState } from 'react';

type UseTableSettingsParams = {
  enabled: boolean;
  pageName?: TABLE_SCHEMA_PAGE_NAME_ENUM;
};

const useTableSettings = ({ enabled, pageName }: UseTableSettingsParams) => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [templates, setTemplates] = useState<Schema[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const requestIdRef = useRef(0);

  const refreshTemplates = useCallback(async (): Promise<boolean> => {
    if (!enabled || !pageName) return false;

    const requestId = ++requestIdRef.current;

    try {
      setTemplatesLoading(true);
      const response = await apiService.getTableSchemaByPageName(pageName);

      if (requestId !== requestIdRef.current) return false;

      if (!response.isSuccess) {
        enqueueSnackbar(
          response.message ||
            formatMessage({
              id: 'datagrid.loadTemplatesError',
              defaultMessage: 'Tablo şablonları yüklenirken bir hata oluştu.',
            }),
          { variant: 'error' }
        );
        return false;
      }

      setTemplates(response.data.schemas);
      return true;
    } catch {
      // HTTP errors are surfaced by the shared response interceptor.
      return false;
    } finally {
      if (requestId === requestIdRef.current) {
        setTemplatesLoading(false);
      }
    }
  }, [enabled, enqueueSnackbar, formatMessage, pageName]);

  useEffect(() => {
    if (!enabled || !pageName) {
      setTemplates((currentTemplates) => (currentTemplates.length ? [] : currentTemplates));
      setTemplatesLoading(false);
    } else {
      setTemplates([]);
      void refreshTemplates();
    }

    return () => {
      requestIdRef.current += 1;
    };
  }, [enabled, pageName, refreshTemplates]);

  return {
    templates,
    templatesLoading,
    refreshTemplates,
  };
};

export default useTableSettings;
